import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEvents } from '../../api/api';
import CreateEvent from './CreateEvent/CreateEvent';
import Profile from './Profile/Profile';
import birthdayImg from '../../images/birthday.jpg';
import './Dashboard.css';

function Logo() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14" cy="8" r="5" fill="#6C63FF" />
      <circle cx="7" cy="20" r="4" fill="#6C63FF" opacity="0.6" />
      <circle cx="21" cy="20" r="4" fill="#6C63FF" opacity="0.6" />
    </svg>
  );
}

function Dashboard() {
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'createEvent' | 'profile'
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName || 'User';

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await getEvents();

      // Transform backend format to match frontend expectations
      const transformedEvents = data.map(event => ({
        id: event.id,
        title: event.title,
        type: event.eventType,
        date: new Date(event.eventDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        dateShort: new Date(event.eventDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
        time: event.eventTime.substring(0, 5), // "HH:MM:SS" -> "HH:MM"
        location: `${event.city}, ${event.state}`,
        locationFull: `${event.locationName}, ${event.city}, ${event.state}, ${event.country}`,
        message: event.message,
        image: event.imageUrl || birthdayImg,
        confirmed: event.guests?.filter(g => g.rsvpStatus === 'accepted').length || 0,
        pending: event.guests?.filter(g => g.rsvpStatus === 'pending' || !g.rsvpStatus).length || 0,
        shareLink: `${window.location.origin}/invite/${event.id}`,
        guests: event.guests || [],
      }));

      setEvents(transformedEvents);
      if (transformedEvents.length > 0) {
        setSelectedEvent(transformedEvents[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleCopy = () => {
    if (selectedEvent?.shareLink) {
      navigator.clipboard.writeText(selectedEvent.shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <Link to="/" className="dashboard-logo">
          <Logo />
          <span className="dashboard-logo-text">AttendX</span>
        </Link>

        <div className="dashboard-center">
          <button className="dashboard-nav-item" onClick={() => setView('dashboard')}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="#6C63FF" strokeWidth="1.5" />
              <rect x="11" y="1" width="6" height="6" rx="1.5" stroke="#6C63FF" strokeWidth="1.5" />
              <rect x="1" y="11" width="6" height="6" rx="1.5" stroke="#6C63FF" strokeWidth="1.5" />
              <rect x="11" y="11" width="6" height="6" rx="1.5" stroke="#6C63FF" strokeWidth="1.5" />
            </svg>
            <span>Dashboard</span>
            <span className="dashboard-badge">{events.length}</span>
          </button>

          <Link to="/templates" className="dashboard-nav-link">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="2" y="2" width="14" height="14" rx="2" stroke="#6C63FF" strokeWidth="1.5" />
              <path d="M2 7h14M7 2v14" stroke="#6C63FF" strokeWidth="1.5" />
            </svg>
            <span>Templates</span>
          </Link>

          <button className="dashboard-new-event" onClick={() => setView('createEvent')}>
            <span>+</span> New Event
          </button>
        </div>

        <div className="dashboard-user">
          <button className="dashboard-user-info" onClick={() => setView('profile')}>
            <span className="dashboard-user-name">{userName}</span>
            <span className="dashboard-user-email">{user.email || 'user@example.com'}</span>
          </button>
          <button className="dashboard-logout" aria-label="Logout" onClick={handleLogout}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M12 3h3a1 1 0 011 1v10a1 1 0 01-1 1h-3M7 13l4-4m0 0L7 5m4 4H1" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        {view === 'createEvent' ? (
          <CreateEvent
            onCancel={() => setView('dashboard')}
            onSuccess={() => {
              fetchEvents();
              setView('dashboard');
            }}
          />
        ) : view === 'profile' ? (
          <Profile onBack={() => setView('dashboard')} user={user} />
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
            <p>Loading your events...</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#dc2626' }}>
            <p>Error: {error}</p>
            <button onClick={() => window.location.reload()} style={{ marginTop: '16px', padding: '8px 16px', background: '#6C63FF', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Retry
            </button>
          </div>
        ) : events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
            <p style={{ marginBottom: '16px' }}>No events yet. Create your first event!</p>
            <button onClick={() => setView('createEvent')} style={{ padding: '10px 20px', background: '#6C63FF', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
              + New Event
            </button>
          </div>
        ) : (
          <>
            {/* Stats bar */}
            <div className="dashboard-stats">
              <div className="dashboard-stat-card">
                <div className="dashboard-stat-text">
                  <span className="dashboard-stat-label">Total Events</span>
                  <span className="dashboard-stat-value">{events.length}</span>
                </div>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="4" width="18" height="17" rx="2" stroke="#374151" strokeWidth="1.5" />
                  <path d="M3 9h18" stroke="#374151" strokeWidth="1.5" />
                  <path d="M8 2v4M16 2v4" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <div className="dashboard-stat-card">
                <div className="dashboard-stat-text">
                  <span className="dashboard-stat-label">Total Invites</span>
                  <span className="dashboard-stat-value">{events.reduce((sum, e) => sum + e.guests.length, 0)}</span>
                </div>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="9" cy="7" r="3" stroke="#374151" strokeWidth="1.5" />
                  <path d="M3 21c0-3.31 2.69-6 6-6s6 2.69 6 6" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="17" cy="9" r="2" stroke="#374151" strokeWidth="1.5" />
                  <path d="M17 13c1.66 0 3 1.34 3 3" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <div className="dashboard-stat-card">
                <div className="dashboard-stat-text">
                  <span className="dashboard-stat-label">Confirmed</span>
                  <span className="dashboard-stat-value">{events.reduce((sum, e) => sum + e.confirmed, 0)}</span>
                </div>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="4" width="20" height="16" rx="2" stroke="#22c55e" strokeWidth="1.5" />
                  <path d="M2 8l10 6 10-6" stroke="#22c55e" strokeWidth="1.5" />
                  <path d="M9 14l2 2 4-4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="dashboard-stat-card">
                <div className="dashboard-stat-text">
                  <span className="dashboard-stat-label">Pending</span>
                  <span className="dashboard-stat-value">{events.reduce((sum, e) => sum + e.pending, 0)}</span>
                </div>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#ef4444" strokeWidth="1.5" />
                  <path d="M12 7v5l3 3" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>

            {/* Two-column layout */}
            <div className="dashboard-content">
              {/* Left — event list */}
              <div className="dashboard-left">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className={`dashboard-event-card ${selectedEvent?.id === event.id ? 'dashboard-event-card--active' : ''}`}
                    onClick={() => setSelectedEvent(event)}
                  >
                    <img src={event.image} alt={event.title} className="dashboard-event-img" />
                    <div className="dashboard-event-info">
                      <div className="dashboard-event-header">
                        <h3 className="dashboard-event-title">{event.title}</h3>
                        <span className="dashboard-event-dots">&#8943;</span>
                      </div>
                      <span className="dashboard-event-type">{event.type}</span>
                      <div className="dashboard-event-meta">
                        <span><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="2" width="12" height="10" rx="1" stroke="#6b7280" strokeWidth="1.2" /><path d="M1 5h12" stroke="#6b7280" strokeWidth="1.2" /></svg>{event.dateShort}</span>
                        <span><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="#6b7280" strokeWidth="1.2" /><path d="M7 3.5v3.5l2.5 1.5" stroke="#6b7280" strokeWidth="1.2" strokeLinecap="round" /></svg>{event.time}</span>
                        <span><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="5" r="2" stroke="#6b7280" strokeWidth="1.2" /><path d="M2.5 12c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5" stroke="#6b7280" strokeWidth="1.2" strokeLinecap="round" /></svg>{event.location}</span>
                      </div>
                      <div className="dashboard-event-guests">
                        <span>Guests</span>
                        <span>{event.guests.length}</span>
                      </div>
                      <div className="dashboard-event-badges">
                        <span className="dashboard-badge-confirmed">{event.confirmed}<br />Confirmed</span>
                        <span className="dashboard-badge-pending">{event.pending}<br />Pending</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right — event detail */}
              <div className="dashboard-right">
                {/* Detail panel */}
                <div className="dashboard-detail-card">
                  <img src={selectedEvent.image} alt={selectedEvent.title} className="dashboard-detail-img" />
                  <div className="dashboard-detail-body">
                    <h2 className="dashboard-detail-title">{selectedEvent.title}</h2>
                    <div className="dashboard-detail-row">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="3" width="14" height="12" rx="1.5" stroke="#6b7280" strokeWidth="1.3" /><path d="M2 7h14" stroke="#6b7280" strokeWidth="1.3" /></svg>
                      <div><span className="dashboard-detail-label">Date</span><span className="dashboard-detail-val">{selectedEvent.date}</span></div>
                    </div>
                    <div className="dashboard-detail-row">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="#6b7280" strokeWidth="1.3" /><path d="M9 5v4l3 2" stroke="#6b7280" strokeWidth="1.3" strokeLinecap="round" /></svg>
                      <div><span className="dashboard-detail-label">Time</span><span className="dashboard-detail-val">{selectedEvent.time}</span></div>
                    </div>
                    <div className="dashboard-detail-row">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="6" r="2.5" stroke="#6b7280" strokeWidth="1.3" /><path d="M4 15c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="#6b7280" strokeWidth="1.3" strokeLinecap="round" /></svg>
                      <div><span className="dashboard-detail-label">Location</span><span className="dashboard-detail-val">{selectedEvent.location}</span></div>
                    </div>
                    <div className="dashboard-detail-message">
                      <span className="dashboard-detail-label">Message</span>
                      <span className="dashboard-detail-val">{selectedEvent.message}</span>
                    </div>
                  </div>
                </div>

                {/* Share Invitation */}
                <div className="dashboard-share-card">
                  <h3 className="dashboard-share-title">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="14" cy="4" r="2" stroke="#1a1a2e" strokeWidth="1.3" /><circle cx="4" cy="9" r="2" stroke="#1a1a2e" strokeWidth="1.3" /><circle cx="14" cy="14" r="2" stroke="#1a1a2e" strokeWidth="1.3" /><path d="M6 8l6-3M6 10l6 3" stroke="#1a1a2e" strokeWidth="1.3" strokeLinecap="round" /></svg>
                    Share Invitation
                  </h3>
                  <span className="dashboard-share-label">Link</span>
                  <div className="dashboard-share-link-row">
                    <input type="text" readOnly value={selectedEvent.shareLink} className="dashboard-share-input" />
                    <button className="dashboard-share-copy" onClick={handleCopy}>{copied ? 'Copied!' : 'Copy'}</button>
                  </div>
                  <div className="dashboard-share-buttons">
                    <button className="dashboard-share-whatsapp">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.36 2.64A7.5 7.5 0 108 15.5a7.46 7.46 0 005.36-2.14l.01 2.14 1.5-.5V2.64zM5.5 8.5c.5 1 1.2 1.9 2.1 2.5l.7-1.4a3.1 3.1 0 01-1.2-1.2L5.5 8.5z" fill="white" /></svg>
                      WhatsApp
                    </button>
                    <button className="dashboard-share-viewpage">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11 3h2v2M9 5l4-4M13 8v4a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      View Page
                    </button>
                  </div>
                </div>

                {/* Guests list */}
                <div className="dashboard-guests-card">
                  <h3 className="dashboard-guests-title">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="6" cy="6" r="3" stroke="#1a1a2e" strokeWidth="1.3" /><path d="M1 16c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="#1a1a2e" strokeWidth="1.3" strokeLinecap="round" /><circle cx="13" cy="7" r="2" stroke="#1a1a2e" strokeWidth="1.3" /><path d="M13 11c1.66 0 3 1.34 3 3" stroke="#1a1a2e" strokeWidth="1.3" strokeLinecap="round" /></svg>
                    Guests ({selectedEvent.guests.length})
                  </h3>
                  {selectedEvent.guests.map((guest, i) => (
                    <div key={i} className="dashboard-guest-row">
                      <div className="dashboard-guest-info">
                        <span className="dashboard-guest-name">{guest.name}</span>
                        <span className="dashboard-guest-email">{guest.email}</span>
                      </div>
                      <span className={`dashboard-guest-status dashboard-guest-status--${guest.status}`}>{guest.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
